import HookFormTextField, {
  HookFormTextFieldProps,
} from '~/components/atoms/HookForm/TextField/HookFormTextField.tsx';
import {
  AddressFinder,
  AddressFinderOption,
  GridLayoutContainer,
  GridLayoutItem,
  HookFormSelect,
  Typography,
} from '~/index.ts';
import { STATE_OPTIONS } from '~/utils/constants.ts';
import { HookFormSelectProps } from '~/components/atoms/HookForm/Select/HookFormSelect.tsx';
import { AddressSchema, CustomerSchema } from '../CustomerFormProvider.tsx';
import {
  CustomerSectionProps,
  CustomerFormLayoutOverrides,
} from '../CustomerForm.tsx';
import { ReactNode } from 'react';
import { isEmpty, values } from 'lodash-es';

export interface LocationSectionProps extends CustomerSectionProps {
  currentAddress?: AddressSchema;
  counties?: Array<string>;
  addressFinderOptions?: AddressFinderOption[];
  onAddressChange?: (address: string) => void;
  onSelectAddress: (selectedAddress: AddressFinderOption | null) => void;
  layoutOverrides?: CustomerFormLayoutOverrides;
}

const CoapplicantLocationSection: React.FC<LocationSectionProps> = ({
  formMode,
  isOptional,
  currentAddress,
  isEditing,
  control,
  formTexts,
  layoutOverrides,
  counties,
  addressFinderOptions,
  onAddressChange,
  onSelectAddress,
  disabled,
}) => {
  const renderEditFields = (): ReactNode => {
    type AllowedTextFieldProps = Omit<
      HookFormTextFieldProps<CustomerSchema>,
      'control'
    >;
    type AllowedSelectProps = Omit<
      HookFormSelectProps<CustomerSchema>,
      'control' | 'onChange'
    >;
    const fieldDefinitions: Array<
      | {
          flexPercentage: number;
          isSelect: false;
          inputProps: AllowedTextFieldProps;
        }
      | {
          flexPercentage: number;
          isSelect: true;
          inputProps: AllowedSelectProps;
        }
    > = [
      {
        inputProps: {
          name: 'coapplicantAddress.apt',
          placeholder: formTexts.coapplicantAddress.apt,
        },
        flexPercentage: 30,
        isSelect: false,
      },
      {
        inputProps: {
          name: 'coapplicantAddress.city',
          placeholder: formTexts.coapplicantAddress.city,
        },
        flexPercentage: 100,
        isSelect: false,
      },
      {
        inputProps: {
          name: 'coapplicantAddress.state',
          placeholder: formTexts.coapplicantAddress.state,
          source: STATE_OPTIONS,
        },
        flexPercentage: 50,
        isSelect: true,
      },
      {
        inputProps: {
          name: 'coapplicantAddress.zip',
          placeholder: formTexts.coapplicantAddress.zip,
        },
        flexPercentage: 50,
        isSelect: false,
      },
      {
        inputProps: {
          name: 'coapplicantAddress.county',
          placeholder: formTexts.coapplicantAddress.county,
          source: counties || [],
        },
        flexPercentage: 100,
        isSelect: true,
      },
    ];

    return (
      <>
        <GridLayoutItem key="streetAddress" flexPercentage={70}>
          <AddressFinder
            options={addressFinderOptions ?? []}
            onSelectAddress={onSelectAddress}
            onInputChange={(inputValue: string) =>
              onAddressChange?.(inputValue)
            }
            control={control}
            styleOverrides={{ width: '100%' }}
            size={layoutOverrides?.select?.size}
            name="coapplicantAddress.streetAddress"
            placeholder={formTexts.coapplicantAddress.streetAddress}
            disabled={disabled}
          />
        </GridLayoutItem>
        {fieldDefinitions.map((fieldDef) => {
          return (
            <GridLayoutItem
              key={fieldDef.inputProps.name}
              flexPercentage={fieldDef.flexPercentage}
              containerSpacing={3}
            >
              {!fieldDef.isSelect && (
                <HookFormTextField
                  fullWidth
                  control={control}
                  size={layoutOverrides?.textField?.size}
                  disabled={disabled}
                  {...fieldDef.inputProps}
                />
              )}
              {fieldDef.isSelect && (
                <HookFormSelect
                  fullWidth
                  control={control}
                  size={layoutOverrides?.textField?.size}
                  disabled={disabled}
                  {...fieldDef.inputProps}
                />
              )}
            </GridLayoutItem>
          );
        })}
      </>
    );
  };

  const isEmptyAddress = (address: AddressSchema): boolean =>
    isEmpty(address) || values(address).every(isEmpty);

  const renderPreviewFields = (): ReactNode => {
    if (!currentAddress || isEmptyAddress(currentAddress)) {
      return (
        <Typography styleOverrides={{ color: 'text.secondary' }}>
          Not required
        </Typography>
      );
    }

    return (
      <>
        <GridLayoutItem>
          <Typography styleOverrides={{ color: 'text.secondary' }}>
            {currentAddress.streetAddress}{' '}
            {currentAddress.apt && `#${currentAddress.apt}`}
          </Typography>
        </GridLayoutItem>
        <GridLayoutItem>
          <Typography styleOverrides={{ color: 'text.secondary' }}>
            {`${currentAddress.city && `${currentAddress.city},`} ${
              currentAddress.state
            } ${currentAddress.zip}`}
          </Typography>
        </GridLayoutItem>
        <GridLayoutItem>
          <Typography styleOverrides={{ color: 'text.secondary' }}>
            {currentAddress.county}
          </Typography>
        </GridLayoutItem>
      </>
    );
  };

  const showEditFields = isEditing || (formMode === 'add' && !isOptional);
  const spacing = showEditFields ? 3 : 1;
  const sectionContent = showEditFields
    ? renderEditFields()
    : renderPreviewFields();
  return (
    <GridLayoutContainer spacing={spacing}>
      {sectionContent}
    </GridLayoutContainer>
  );
};

export default CoapplicantLocationSection;
